import { useState, useRef, useEffect } from "react";

const YT_KEYS = [
  "AIzaSyBu33AChZxQMJUoWwn0JGBhbK2arc190Mk",
  "AIzaSyCJTXZBa-PJDCbGyd_jmf0zvUGp4o9XiRI",
  "AIzaSyBDHvM-A0K1bBp7rCxR4rewLcX20_nmPIA",
  "AIzaSyDf0wMM0sqXycc6BEFstd6bvm-fyIZmPyg"
];

function AudioPlayer({ audioRef, isPlaying }) {
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const update = () => setProgress(audio.currentTime);
    const setDur = () => setDuration(audio.duration);
    audio.addEventListener("timeupdate", update);
    audio.addEventListener("loadedmetadata", setDur);
    return () => { audio.removeEventListener("timeupdate", update); audio.removeEventListener("loadedmetadata", setDur); };
  }, [audioRef]);

  function seek(e) {
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    if (audioRef.current) audioRef.current.currentTime = pct * duration;
  }

  function fmt(s) { return `${Math.floor(s/60)}:${(Math.floor(s%60)<10?"0":"")}${Math.floor(s%60)}`; }

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ height: 6, background: "#1a1a1a", borderRadius: 3, cursor: "pointer", marginBottom: 8 }} onClick={seek}>
        <div style={{ width: `${duration ? (progress/duration)*100 : 0}%`, height: "100%", background: "#1db954", borderRadius: 3, transition: "width 0.1s" }} />
      </div>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <span style={{ fontSize: 11, color: "#555" }}>{fmt(progress)}</span>
        <span style={{ fontSize: 11, color: "#555" }}>{fmt(duration)}</span>
      </div>
    </div>
  );
}
export default function Karaoke({ song, lyrics, currentTime, onClose, playerRef, setVideoId }) {
  const [mode, setMode] = useState("ready"); // ready, loading, singing, finished
  const [pitchLevel, setPitchLevel] = useState(0);
  const [recordingUrl, setRecordingUrl] = useState(null);
  const [isPlayingBack, setIsPlayingBack] = useState(false);
  const [syncDelay, setSyncDelay] = useState(350);
const recordStartTimeRef = useRef(0);
const musicStartTimeRef = useRef(0);
  const [karaokeFound, setKaraokeFound] = useState(false);
  const [originalVideoId, setOriginalVideoId] = useState(null);

  const mediaStreamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const animFrameRef = useRef(null);
  const chunksRef = useRef([]);
  const lyricsRef = useRef(null);
  const audioRef = useRef(null);

  // Get lyrics
  const syncedLines = lyrics?.type === "synced" ? lyrics.lines : [];
  const plainLines = lyrics?.type === "plain" ? lyrics.lines : [];
 const allLines = syncedLines.length > 0 ? syncedLines : plainLines.map(t => ({ text: t.text || t, time: t.startTime || -1 }));

  const activeIndex = syncedLines.length > 0
    ? syncedLines.findIndex((l, i) =>
        currentTime >= l.time && (i === syncedLines.length - 1 || currentTime < syncedLines[i + 1].time))
    : -1;

  useEffect(() => {
    if (lyricsRef.current && activeIndex >= 0) {
      const active = lyricsRef.current.querySelector(".k-active");
      if (active) active.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [activeIndex]);

  useEffect(() => {
    return () => {
      cancelAnimationFrame(animFrameRef.current);
      if (mediaStreamRef.current) mediaStreamRef.current.getTracks().forEach(t => t.stop());
      if (audioCtxRef.current) audioCtxRef.current.close().catch(() => {});
    };
  }, []);

  async function findKaraokeTrack() {
    setMode("loading");
    const queries = [
      `${song.trackName} ${song.artistName} karaoke instrumental`,
      `${song.trackName} karaoke version no vocals`,
      `${song.trackName} instrumental backing track`,
    ];
    for (const key of YT_KEYS) {
      for (const q of queries) {
        try {
          const r = await fetch(`https://www.googleapis.com/youtube/v3/search?part=snippet&q=${encodeURIComponent(q)}&key=${key}&maxResults=5&type=video`);
          const d = await r.json();
          if (d.items?.length > 0) {
            const vid = d.items[0].id.videoId;
            // Save original video id to restore later
            setOriginalVideoId(playerRef.current?.getVideoData?.()?.video_id || null);
            // Switch player to karaoke track
            setVideoId(vid);
            setKaraokeFound(true);
            setTimeout(() => {
              playerRef.current?.setVolume(0);
              playerRef.current?.seekTo(0, true);
              playerRef.current?.playVideo();
              // Fade in smoothly
              let vol = 0;
              const fadeIn = setInterval(() => {
                vol += 4;
                playerRef.current?.setVolume(Math.min(vol, 80));
                if (vol >= 80) clearInterval(fadeIn);
              }, 80);
              startRecording();
           }, syncDelay);
            return;
          }
        } catch {}
      }
    }
    // No karaoke found — use original at low volume with fade in
    setKaraokeFound(false);
    playerRef.current?.setVolume(0);
    playerRef.current?.seekTo(0, true);
    playerRef.current?.playVideo();
    let vol = 0;
    const fadeIn = setInterval(() => {
      vol += 3;
      playerRef.current?.setVolume(Math.min(vol, 30));
      if (vol >= 30) clearInterval(fadeIn);
    }, 100);
    startRecording();
  }

  async function startRecording() {
    try {
    const stream = await navigator.mediaDevices.getUserMedia({ 
  audio: {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
  }
});
    
      mediaStreamRef.current = stream;

      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      audioCtxRef.current = ctx;
      const analyser = ctx.createAnalyser();
      analyserRef.current = analyser;
      analyser.fftSize = 2048;
      ctx.createMediaStreamSource(stream).connect(analyser);

      const recorder = new MediaRecorder(stream, { 
  mimeType: MediaRecorder.isTypeSupported("audio/webm;codecs=opus") 
    ? "audio/webm;codecs=opus" 
    : "audio/webm",
  audioBitsPerSecond: 128000
});
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];
      recorder.ondataavailable = e => chunksRef.current.push(e.data);
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setRecordingUrl(URL.createObjectURL(blob));
      };
      recorder.start();
recordStartTimeRef.current = Date.now();
musicStartTimeRef.current = playerRef.current?.getCurrentTime() || 0;
      setMode("singing");
      detectVoice();
    } catch {
      alert("Please allow microphone access!");
      playerRef.current?.setVolume(100);
      setMode("ready");
    }
  }

  function detectVoice() {
    const analyser = analyserRef.current;
    if (!analyser) return;
    const buffer = new Float32Array(analyser.fftSize);
    function loop() {
      analyser.getFloatTimeDomainData(buffer);
      let sum = 0;
      for (let i = 0; i < buffer.length; i++) sum += buffer[i] * buffer[i];
      setPitchLevel(Math.min(100, Math.round(Math.sqrt(sum / buffer.length) * 600)));
      animFrameRef.current = requestAnimationFrame(loop);
    }
    loop();
  }

  function stopKaraoke() {
    cancelAnimationFrame(animFrameRef.current);
    mediaRecorderRef.current?.stop();
    if (mediaStreamRef.current) mediaStreamRef.current.getTracks().forEach(t => t.stop());
    if (audioCtxRef.current) audioCtxRef.current.close().catch(() => {});
    // Pause music so user can listen to their recording
    playerRef.current?.pauseVideo();
    playerRef.current?.setVolume(100);
    setMode("finished");
    setPitchLevel(0);
  }

  function togglePlayback() {
    if (!audioRef.current) return;
    if (isPlayingBack) {
      audioRef.current.pause();
      playerRef.current?.pauseVideo();
      setIsPlayingBack(false);
    } else {
      // Restart music from beginning at low volume
      setIsPlayingBack(true);
audioRef.current.currentTime = 0;
audioRef.current.play();
// Delay music start so voice and music sync
setTimeout(() => {
  playerRef.current?.seekTo(0, true);
  playerRef.current?.playVideo();
  playerRef.current?.setVolume(40);
}, 300);
      // Small delay to let music buffer — 500ms is more natural
      // Start voice at the right offset based on syncDelay
audioRef.current.currentTime = 0;
audioRef.current.play();
    }
  }

  function handleClose() {
  cancelAnimationFrame(animFrameRef.current);
  if (mediaStreamRef.current) mediaStreamRef.current.getTracks().forEach(t => t.stop());
  if (audioCtxRef.current) audioCtxRef.current.close().catch(() => {});
  playerRef.current?.setVolume(100);
  // Restore original song if karaoke track was loaded
  if (originalVideoId) {
    setVideoId(originalVideoId);
    setTimeout(() => {
      playerRef.current?.playVideo();
    }, 1000);
  } else {
    playerRef.current?.playVideo();
  }
  onClose();
}

  function tryAgain() {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; }
    setMode("ready");
    setRecordingUrl(null);
    setIsPlayingBack(false);
    chunksRef.current = [];
    setPitchLevel(0);
  }

  function shareRecording() {
    const text = `🎤 I just sang "${song.trackName}" by ${song.artistName} on MyMusic!\nhttps://mymusic-33737.web.app`;
    if (navigator.share) navigator.share({ title: "MyMusic Karaoke", text });
    else { navigator.clipboard.writeText(text); alert("Link copied!"); }
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: "#050505", zIndex: 502, display: "flex", flexDirection: "column", fontFamily: "'DM Sans',sans-serif", color: "#fff", paddingBottom: 60 }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=DM+Sans:wght@400;500;600&display=swap');
        @keyframes blink { 0%,100%{opacity:1} 50%{opacity:0.3} }
        @keyframes spin { to{transform:rotate(360deg)} }
      `}</style>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 20px", borderBottom: "1px solid #111", flexShrink: 0 }}>
        <button onClick={handleClose} style={{ background: "#111", border: "1px solid #222", color: "#fff", width: 34, height: 34, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
          <svg viewBox="0 0 24 24" style={{ width: 14, height: 14, fill: "none", stroke: "#fff", strokeWidth: 2 }}><polyline points="15 18 9 12 15 6"/></svg>
        </button>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 15, fontWeight: 800 }}>🎤 Karaoke</div>
          <div style={{ fontSize: 11, color: "#555" }}>{song.trackName} — {song.artistName}</div>
        </div>
        {mode === "singing" ? (
          <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#e91429", animation: "blink 1s infinite" }} />
            <span style={{ fontSize: 10, color: "#e91429", fontWeight: 700 }}>REC</span>
          </div>
        ) : <div style={{ width: 44 }} />}
      </div>

      {/* Loading */}
      {mode === "loading" && (
        <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 20 }}>
          <div style={{ width: 48, height: 48, border: "3px solid #222", borderTopColor: "#1db954", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
          <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 17, fontWeight: 800 }}>Finding karaoke track...</div>
          <div style={{ fontSize: 13, color: "#555" }}>Searching for instrumental version</div>
        </div>
      )}

      {/* Lyrics */}
      {(mode === "singing" || mode === "ready") && (
        <div ref={lyricsRef} style={{ flex: 1, overflowY: "auto", padding: "20px 24px 220px", display: "flex", flexDirection: "column", alignItems: "center" }}>
          {allLines.length === 0 ? (
            <div style={{ textAlign: "center", paddingTop: 80 }}>
              <div style={{ fontSize: 56, marginBottom: 20 }}>🎤</div>
              <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 20, fontWeight: 800, marginBottom: 10 }}>Sing Along!</div>
              <div style={{ fontSize: 14, color: "#444", lineHeight: 1.8, maxWidth: 260 }}>No lyrics available. Sing along with the music — your voice will be recorded!</div>
            </div>
          ) : allLines.map((line, i) => {
            const isActive = i === activeIndex;
            const isNext = activeIndex >= 0 && i > activeIndex && i <= activeIndex + 2;
            const isPast = activeIndex >= 0 && i < activeIndex;
            return (
              <p key={i} className={isActive ? "k-active" : ""} style={{
                fontSize: isActive ? 24 : isNext ? 17 : 14,
                fontWeight: isActive ? 800 : isNext ? 500 : 400,
                color: isActive ? "#fff" : isNext ? "#777" : isPast ? "#222" : "#444",
                lineHeight: 1.9, marginBottom: 10,
                transition: "all 0.3s ease",
                textAlign: "center", maxWidth: 360,
              }}>
                {line.text || line}
              </p>
            );
          })}
        </div>
      )}

      {/* Finished screen */}
      {mode === "finished" && (
        <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", alignItems: "center", padding: "32px 24px 140px" }}>
          <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 20, fontWeight: 800, marginBottom: 8 }}>Recording Complete! 🎤</div>
          <div style={{ fontSize: 13, color: "#555", marginBottom: 28 }}>Listen to your voice below</div>

          {/* Playback */}
          {recordingUrl && (
            <div style={{ width: "100%", maxWidth: 340, background: "#111", borderRadius: 20, padding: 20, marginBottom: 20, border: "1px solid #1a1a1a" }}>
              <audio ref={audioRef} src={recordingUrl} onEnded={() => { setIsPlayingBack(false); playerRef.current?.pauseVideo(); playerRef.current?.setVolume(100); }} style={{ display: "none" }} />
              
             {/* Audio progress bar - clickable */}
<AudioPlayer audioRef={audioRef} isPlaying={isPlayingBack} />

              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <button onClick={togglePlayback} style={{ width: 56, height: 56, borderRadius: "50%", background: isPlayingBack ? "#e91429" : "#1db954", border: "none", color: "#000", fontSize: 22, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  {isPlayingBack ? "⏸" : "▶"}
                </button>
                <div>
                  <div style={{ fontFamily: "'Syne',sans-serif", fontSize: 15, fontWeight: 700 }}>Your Voice</div>
                  <div style={{ fontSize: 12, color: "#555", marginTop: 2 }}>{song.trackName}</div>
                </div>
              </div>
              {isPlayingBack && <p style={{ fontSize: 11, color: "#333", marginTop: 12, textAlign: "center" }}>🎵 Playing your recording...</p>}
              <div style={{ marginTop: 14 }}>
  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
    <span style={{ fontSize: 11, color: "#555" }}>⏱ Voice sync</span>
    <span style={{ fontSize: 11, color: "#1db954" }}>{syncDelay}ms</span>
  </div>
  <input type="range" min="0" max="1000" step="50" value={syncDelay} onChange={e => { setSyncDelay(Number(e.target.value)); if (isPlayingBack && audioRef.current && playerRef.current) { const musicTime = playerRef.current.getCurrentTime() || 0; const offset = Number(e.target.value) / 1000; audioRef.current.currentTime = Math.max(0, musicTime - offset); } }} style={{ width: "100%", accentColor: "#1db954" }} />
  <div style={{ fontSize: 10, color: "#333", marginTop: 4, textAlign: "center" }}>Slide to fix sync if voice is early or late</div>
</div>
            </div>
          )}

          <div style={{ width: "100%", maxWidth: 340, display: "flex", flexDirection: "column", gap: 10 }}>
            <button onClick={shareRecording} style={{ padding: 14, borderRadius: 50, border: "none", background: "#1db954", color: "#000", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>📱 Share Recording</button>
            <button onClick={tryAgain} style={{ padding: 14, borderRadius: 50, border: "1px solid #2a2a2a", background: "transparent", color: "#fff", fontSize: 14, cursor: "pointer" }}>🔄 Try Again</button>
            <button onClick={handleClose} style={{ background: "none", border: "none", color: "#444", fontSize: 13, cursor: "pointer", padding: 8 }}>▶ Resume Music & Close</button>
          </div>
        </div>
      )}

      {/* Bottom controls */}
      {mode !== "finished" && mode !== "loading" && (
        <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, background: "rgba(5,5,5,0.97)", borderTop: "1px solid #111", padding: "16px 20px 36px", backdropFilter: "blur(20px)" }}>
          {mode === "singing" && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                <span style={{ fontSize: 11, color: "#555" }}>🎤 Your voice</span>
                <span style={{ fontSize: 11, color: pitchLevel > 20 ? "#1db954" : "#555" }}>
                  {pitchLevel > 20 ? "🎵 Singing detected!" : "Sing louder..."}
                </span>
              </div>
              <div style={{ height: 6, background: "#111", borderRadius: 3, overflow: "hidden" }}>
                <div style={{ width: `${pitchLevel}%`, height: "100%", background: pitchLevel > 60 ? "#1db954" : pitchLevel > 30 ? "#f59e0b" : "#e91429", borderRadius: 3, transition: "width 0.08s" }} />
              </div>
            </div>
          )}

          {mode === "ready" && (
            <>
              <button onClick={findKaraokeTrack} style={{ width: "100%", padding: 15, borderRadius: 50, border: "none", background: "linear-gradient(135deg, #1db954, #0a8f3c)", color: "#000", fontSize: 15, fontWeight: 700, cursor: "pointer", marginBottom: 10 }}>
                🎤 Start Karaoke
              </button>
              <p style={{ textAlign: "center", fontSize: 11, color: "#333" }}>Finds instrumental track · Records your voice · Listen back!</p>
            </>
          )}

          {mode === "singing" && (
            <button onClick={stopKaraoke} style={{ width: "100%", padding: 15, borderRadius: 50, border: "none", background: "#e91429", color: "#fff", fontSize: 15, fontWeight: 700, cursor: "pointer" }}>
              ⏹ Stop & Listen Back
            </button>
          )}
        </div>
      )}
    </div>
  );
}